import router from '../../router';
import axios from 'axios';
import dayjs from "dayjs";

const nameVersionPath = (name, version) => `${encodeURIComponent(name)}/versions/${version}`;
const withoutEmptyVersion = ({version, ...rest}) => (version === '' || version == null ? rest : {...rest, version});

const store = {
    namespaced: true,
    state: {
        users: [],
        clientList: [],
        tokens:[],
        clientTokens:[],
        clientProducts:[],
        encryptedUrlList:[],
        products:[],
        entitlements:[],
        productEntitlementList:[],
        jobTemplates:[],
        jobTemplateGroups:[],
        jobTemplateGroupMapList:[],
        agentsUsageData: {},
        agentsUsageDataStartDate: dayjs().startOf('month').format("YYYY-MM-DD"),
        agentsUsageDataEndDate: dayjs().format("YYYY-MM-DD"),
        agentsUsageMonth: dayjs().format('MM'),
        mcpUsageData: {}, // keyed by MCP server: { mcp: { daily, aggregate }, mcp_control: {...} }
        mcpUsageDataStartDate: dayjs().startOf('month').format("YYYY-MM-DD"),
        mcpUsageDataEndDate: dayjs().format("YYYY-MM-DD"),
        mcpUsageMonth: dayjs().format('MM'),
        infraUsageData: {},
        infraUsageDataStartDate: dayjs().startOf('month').format("YYYY-MM-DD"),
        infraUsageDataEndDate: dayjs().format("YYYY-MM-DD"),        
        usageMonth: dayjs().format('MM'),
        entitlementUsageMonth: dayjs().format('MM'),
        infraMeterLabels: {
            aMDPObjectStorage: {
              label: "aMDPObjectStorage",
              unit: "TB",
              isMetered: true
            },
            aMDPServerlessScan: {
              label: "aMDPServerlessScan",
              unit: "TB",
              isMetered: true
            },
            aMDPEgressBandwidth: {
              label: "aMDPEgressBandwidth",
              unit: "TB",
              isMetered: true
            },
            DynamoDBReadUnits: {
              label: "DynamoDBReadUnits",
              unit: "ReadRequestUnits",
              isMetered: false
            },
            DynamoDBWriteUnits: {
              label: "DynamoDBWriteUnits",
              unit: "WriteRequestUnits",
              isMetered: false
            },
            DynamoDBImportData: {
              label: "DynamoDBImportData",
              unit: "TB",
              isMetered: false
            },
            DynamoDBStorage: {
              label: "DynamoDBStorage",
              unit: "TB",
              isMetered: false  
            },
            GlueDPUHours: {
              label: "GlueDPUHours",
              unit: "DPU-Hour",
              isMetered: false  
            },
            QuickSightUsers: {
              label: "QuickSightUsers",
              unit: "Users",
              isMetered: false
            },
            GbHours: {
              label: "GbHours",
              unit: "GB-Hours",
              isMetered: false
            },
            CpuHours: {
              label: "CpuHours",
              unit: "vCPU-Hours",
              isMetered: false
            }
        },
        configurations: [],
        docLinks: []
        

    },
    mutations: {
        setUsers(state, users) {
            state.users = users
        },
        setClientList(state, clientList) {
            state.clientList = clientList
        },
        setTokens(state, tokens) {
            state.tokens = tokens
        },
        setClientTokens(state, clientTokens) {
            state.clientTokens = clientTokens
        },
        setEncryptedUrlList(state, encryptedUrlList) {
            state.encryptedUrlList = encryptedUrlList
        },
        setClientProducts(state, clientProducts) {
            state.clientProducts = clientProducts
        },
        setProducts(state, products) {
            state.products = products
        },
        setEntitlements(state, entitlements) {
            state.entitlements = entitlements
        },
        setProductEntitlementList(state, productEntitlementList) {
            state.productEntitlementList = productEntitlementList
        },
        setJobTemplates(state, jobTemplates) {
            state.jobTemplates = jobTemplates
        },
        setJobTemplateGroups(state, jobTemplateGroups) {
            state.jobTemplateGroups = jobTemplateGroups
        },
        setJobTemplateGroupMapList(state, jobTemplateGroupMapList) {
            state.jobTemplateGroupMapList = jobTemplateGroupMapList
        },
        setAgentsUsageData(state, data) {
            state.agentsUsageData = data
        },
        setAgentsUsageDataStartDate(state, startDate) {
            state.agentsUsageDataStartDate = startDate
        },
        setAgentsUsageDataEndDate(state, endDate) {
            state.agentsUsageDataEndDate = endDate
        },
        setAgentsUsageMonth(state, month) {
            state.agentsUsageMonth = month
        },
        setMcpUsageData(state, data) {
            state.mcpUsageData = data
        },
        setMcpUsageDataStartDate(state, startDate) {
            state.mcpUsageDataStartDate = startDate
        },
        setMcpUsageDataEndDate(state, endDate) {
            state.mcpUsageDataEndDate = endDate
        },
        setMcpUsageMonth(state, month) {
            state.mcpUsageMonth = month
        },
        setInfraUsageData(state, data) {
            state.infraUsageData = data
        },
        setInfraUsageDataStartDate(state, startDate) {
            state.infraUsageDataStartDate = startDate
        },
        setInfraUsageDataEndDate(state, endDate) {
            state.infraUsageDataEndDate = endDate
        },
        setUsageMonth(state, month) {
            state.usageMonth = month
        },
        setEntitlementUsageMonth(state, month) {
            state.entitlementUsageMonth = month
        },
        setDocLinks(state, docLinks) {
            state.docLinks = docLinks
        },
    },
    actions: {
        onClientChange({ commit }) {
            commit('setAgentsUsageData', {}) // Clear agents usage data
            commit('setMcpUsageData', {}) // Clear MCP usage data
        },
        async createAUTClient({rootState}, clientPayload) {
            return axios.post(rootState.autMgmtUrl + "/v1/staging/clients", clientPayload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async updateAUTClient({rootState}, clientPayload) {
            const eTag = clientPayload.eTag
            delete clientPayload.eTag
            const headers = {
                Authorization: `Bearer ${rootState.accessToken}`,
            }
            if (eTag) {
                headers['If-Match'] = eTag
            }
            return axios.put(rootState.autMgmtUrl + "/v1/staging/clients/" + clientPayload.cid, clientPayload, {
                headers,
            })
        },
        async deleteAUTClient({rootState}, clientPayload) {
            return axios.delete(rootState.autMgmtUrl + "/v1/staging/clients/" + clientPayload.cid, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async readAUTClient({rootState}, id) {
            return axios.get(rootState.autMgmtUrl + `/v1/staging/clients/${id}?detail=pixel`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async createClientV2({rootState}, clientPayload) {
            return axios.post(rootState.clientMgmtUrl + "/v1/cids", clientPayload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then(function(data) {
                const cidObj = {name: clientPayload.name, cid: clientPayload.cid}
                return axios.put(rootState.authUrl + '/v1/clients/' + cidObj.cid, cidObj, {
                    headers: {
                        Authorization: `Bearer ${rootState.accessToken}`,
                    },
                }).then(function(data) {
                    //add new client to current user's client list
                    return axios.get(rootState.authUrl +  '/v1/users/' + rootState.currentUser.email, {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`,
                        },
                    }).then((response) => {
                        if(response.data.cids){
                            response.data.cids.push(cidObj.cid)
                            return axios.put(rootState.authUrl +  '/v1/users/' + rootState.currentUser.email, response.data, {
                                headers: {
                                    Authorization: `Bearer ${rootState.accessToken}`,
                                },
                            })
                        }
                    });
                })
            })
        },
        async updateClientV2({rootState}, clientPayload) {
            return axios.put(rootState.clientMgmtUrl + "/v1/cids/" + clientPayload.cid, clientPayload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async deleteClientV2({rootState}, clientPayload) {
            return axios.delete(rootState.clientMgmtUrl + "/v1/cids/" + clientPayload.cid, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then(function(data) {
                return axios.delete(rootState.authUrl + '/v1/clients/' + clientPayload.cid, {
                    headers: {
                        Authorization: `Bearer ${rootState.accessToken}`,
                    },
                })
            })
        },
        async readClientV2({rootState}, id) {
            return axios.get(rootState.clientMgmtUrl + `/v1/cids/${id}`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async enableDisableClient({rootState}, clientPayload) {
            return axios.put(rootState.autMgmtUrl + `/v1/clients/${clientPayload.cid}/enabled/${clientPayload.enabled}`, null, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async upsertUser({rootState}, userPayload) {
            if(!userPayload.cids) userPayload.cids = [""]
            return axios.put(rootState.authUrl + "/v1/users/" + userPayload.email, userPayload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async deleteUser({rootState}, userPayload) {
            return axios.delete(rootState.authUrl + "/v1/users/" + userPayload.email, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async readUser({rootState}, id) {
            return axios.get(rootState.authUrl + "/v1/users/" + id, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async generateToken({rootState},userPayload) {
            let sendEmail = userPayload.send_email?"?sendmail=true":"";
            delete userPayload.send_email;
            //this endpoint is for client_admin
            let url=`/v1/clients/${rootState.currentClient.cid}/refresh_tokens${sendEmail}`
            if(rootState.currentUser.roles.includes("aqfer_admin") || rootState.currentUser.roles.includes("aqfer_manager")){
                //this endpoint is for aqfer_admin and aqfer_manager
                url=`/v1/refresh_tokens${sendEmail}`
            }
            return axios.post(rootState.authUrl + url , userPayload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }) 
        },
        async createEncryptedUrl({rootState}, payload) {
            return axios.post(rootState.defaultSurfaceUrl + "/v2/helpjuice_urls", payload, {
                    headers: {
                        Authorization: `Bearer ${rootState.accessToken}`,
                    },
            })
        },
        async updateEncryptedUrl({rootState}, payload) {
            return axios.put(rootState.defaultSurfaceUrl + "/v2/helpjuice_urls/" + payload.uuid, payload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async deleteEncryptedUrl({rootState}, uuid) {
            return axios.delete(rootState.defaultSurfaceUrl + "/v2/helpjuice_urls/" + uuid, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async readEncryptedUrl({rootState}, uuid) {
            return axios.get(rootState.defaultSurfaceUrl + "/v2/helpjuice_urls/" + uuid, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        fetchUsers({rootState, commit}) {
            axios.get(rootState.authUrl + "/v1/users", {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const users = response.data;
                commit('setUsers', users)
            });
        },
        fetchClientList({rootState, commit}){
            axios.get(rootState.clientMgmtUrl+ "/v1/cids", {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                if(response.data){
                    const clientList = response.data;
                    commit('setClientList', clientList)
                }
            });
        },
        async fetchRefreshTokens({rootState, commit}) {
            return axios.get(rootState.authUrl + "/v1/refresh_tokens", {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const tokens = response.data;
                commit('setTokens', tokens)
                return tokens
            });
        },
        async fetchAllRefreshTokens({rootState, state, dispatch, commit}) {
            dispatch('fetchRefreshTokens').then((refreshtokens)=>{
                axios.get(rootState.authUrl + "/v1/refresh_tokens?revoked=true", {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const revokedtokens = response.data;
                commit('setTokens', [...refreshtokens,...revokedtokens])
                })
            });
        },
        async fetchClientRefreshTokens({rootState, commit}) {
            return axios.get(rootState.authUrl + `/v1/clients/${rootState.currentClient.cid}/refresh_tokens`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const tokens = response.data ? response.data: [];
                commit('setClientTokens', tokens)
                return tokens
            });
        },
        async fetchClientsAllRefreshTokens({rootState, dispatch, commit}) {
            dispatch('fetchClientRefreshTokens').then((refreshtokens)=>{
                axios.get(rootState.authUrl + `/v1/clients/${rootState.currentClient.cid}/refresh_tokens?revoked=true`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const revokedtokens = response.data ? response.data: [];
                commit('setClientTokens', [...refreshtokens,...revokedtokens])
                })
            });
        },
        fetchEncryptedUrlList({rootState, commit}){
            axios.get(rootState.defaultSurfaceUrl + "/v2/helpjuice_urls", {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                if(response.data){
                    const encryptedUrlList = response.data;
                    commit('setEncryptedUrlList', encryptedUrlList)
                }
            });
        },
        async readToken({rootState}, id) {
             //this endpoint is for client_admin
             let url=`/v1/clients/${rootState.currentClient.cid}/refresh_tokens/${id}`
             if(rootState.currentUser.roles.includes("aqfer_admin") || rootState.currentUser.roles.includes("aqfer_manager")){
                  //this endpoint is for aqfer_admin and aqfer_manager
                 url=`/v1/refresh_tokens/${id}`
             }
            return axios.get(rootState.authUrl + url, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async revokeToken({rootState}, id){
             //this endpoint is for client_admin
            let url=`/v1/clients/${rootState.currentClient.cid}/refresh_tokens/${id}`
            if(rootState.currentUser.roles.includes("aqfer_admin") || rootState.currentUser.roles.includes("aqfer_manager")){
                 //this endpoint is for aqfer_admin and aqfer_manager
                url=`/v1/refresh_tokens/${id}`
            }
            return axios.delete(rootState.authUrl + url, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async fetchClientProducts({rootState, commit},cid) {
            return axios.get(rootState.clientMgmtUrl + `/v1/clients/${cid}/products`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const products = response.data ? response.data: [];
                commit('setClientProducts', products)
                return products
            });
        },
        async addClientSubscription({rootState}, payload) {
            return axios.post(rootState.clientMgmtUrl + `/v1/clients/${payload.cid}/products`, payload, {
                    headers: {
                        Authorization: `Bearer ${rootState.accessToken}`,
                    },
            })
        },
        async updateClientSubscription({rootState, dispatch}, payload) {
            const product = await dispatch('findProduct', payload.product_id);
            return axios.put(rootState.clientMgmtUrl +  `/v1/clients/${payload.cid}/products/` + nameVersionPath(product.name, product.version), payload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async deleteClientProduct({rootState, dispatch}, payload) {
            const product = await dispatch('findProduct', payload.product_id);
            return axios.delete(rootState.clientMgmtUrl +  `/v1/clients/${payload.cid}/products/` + nameVersionPath(product.name, product.version), {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        fetchEntitlements({rootState, commit}) {
            return axios.get(rootState.clientMgmtUrl + "/v1/entitlements", {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const entitlements = response.data;
                if(entitlements)
                    commit('setEntitlements', entitlements)
            });
        },
        async createEntitlement({rootState}, payload) {
            return axios.post(rootState.clientMgmtUrl + "/v1/entitlements", withoutEmptyVersion(payload), {
                    headers: {
                        Authorization: `Bearer ${rootState.accessToken}`,
                    },
            })
        },
        async findEntitlement({state, dispatch}, entitlementId) {
            if (!state.entitlements.some((item) => item.entitlement_id === entitlementId))
                await dispatch('fetchEntitlements');
            const entitlement = state.entitlements.find((item) => item.entitlement_id === entitlementId);
            if (!entitlement)
                throw new Error(`Entitlement ${entitlementId} not found`);
            return entitlement;
        },
        async updateEntitlement({rootState}, {name, version, data}) {
            return axios.put(rootState.clientMgmtUrl + "/v1/entitlements/" + nameVersionPath(name, version), withoutEmptyVersion(data), {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async deleteEntitlement({rootState}, {name, version}) {
            return axios.delete(rootState.clientMgmtUrl + "/v1/entitlements/" + nameVersionPath(name, version), {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async readEntitlement({rootState}, {name, version}) {
            return axios.get(rootState.clientMgmtUrl + "/v1/entitlements/" + nameVersionPath(name, version), {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        fetchProducts({rootState, commit}) {
            return axios.get(rootState.clientMgmtUrl + "/v1/products", {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const products = response.data;
                if(products)
                    commit('setProducts', products)
            });
        },
        async createProduct({rootState}, payload) {
            return axios.post(rootState.clientMgmtUrl + "/v1/products", withoutEmptyVersion(payload), {
                    headers: {
                        Authorization: `Bearer ${rootState.accessToken}`,
                    },
            })
        },
        async findProduct({state, dispatch}, productId) {
            if (!state.products.some((item) => item.product_id === productId))
                await dispatch('fetchProducts');
            const product = state.products.find((item) => item.product_id === productId);
            if (!product)
                throw new Error(`Product ${productId} not found`);
            return product;
        },
        async updateProduct({rootState}, {name, version, data}) {
            return axios.put(rootState.clientMgmtUrl + "/v1/products/" + nameVersionPath(name, version), withoutEmptyVersion(data), {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async deleteProduct({rootState}, {name, version}) {
            return axios.delete(rootState.clientMgmtUrl + "/v1/products/" + nameVersionPath(name, version), {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async readProduct({rootState}, {name, version}) {
            return axios.get(rootState.clientMgmtUrl + "/v1/products/" + nameVersionPath(name, version), {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        getProductEntitlementList({rootState, commit }, query) {
            return axios.get(rootState.clientMgmtUrl + "/v1/product_entitlements?"+query, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const list = response.data;
                if(list)
                    commit('setProductEntitlementList', list)
                return response
            }).catch((error) =>{
                commit('setProductEntitlementList', [])
                return []
            })
        },
        async addProductEntitlement({rootState}, payload) {
            return axios.post(rootState.clientMgmtUrl + "/v1/product_entitlements", payload, {
                    headers: {
                        Authorization: `Bearer ${rootState.accessToken}`,
                    },
            })
        },
        async updateProductEntitlement({rootState, dispatch}, {product, data}) {
            const entitlement = await dispatch('findEntitlement', data.entitlement_id);
            return axios.put(rootState.clientMgmtUrl + `/v1/product_entitlements/${nameVersionPath(entitlement.name, entitlement.version)}?product=${product.product_id}`, data, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async deleteProductEntitlement({rootState, dispatch}, {product, entitlementId}) {
            const entitlement = await dispatch('findEntitlement', entitlementId);
            return axios.delete(rootState.clientMgmtUrl + `/v1/product_entitlements/${nameVersionPath(entitlement.name, entitlement.version)}?product=${product.product_id}`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        getSubEntitlementList({rootState, commit }, {name, version}) {
            return axios.get(rootState.clientMgmtUrl + `/v1/sub_entitlements/${nameVersionPath(name, version)}`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const list = response.data;
                if(list)
                    commit('setProductEntitlementList', list)
                return response
            }).catch((error) =>{
                commit('setProductEntitlementList', [])
                return []
            })
        },
        async fetchJobTemplates({rootState, commit}) {
            axios.get(rootState.surfaceUrl + `/v1/job_templates`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const jobTemplates = response.data ? response.data: [];
                commit('setJobTemplates', jobTemplates)
            });
        },
        async createJobTemplate({rootState}, payload) {
            return axios.post(rootState.surfaceUrl + "/v1/job_templates", payload, {
                    headers: {
                        Authorization: `Bearer ${rootState.accessToken}`,
                    },
            })
        },
        async updateJobTemplate({rootState}, payload) {
            return axios.put(rootState.surfaceUrl + "/v1/job_templates/" + payload.id, payload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async deleteJobTemplate({rootState}, id) {
            return axios.delete(rootState.surfaceUrl + "/v1/job_templates/" + id, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async readJobTemplate({rootState}, id) {
            return axios.get(rootState.surfaceUrl + "/v1/job_templates/" + id, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async fetchJobTemplateGroups({rootState, commit}) {
            axios.get(rootState.surfaceUrl + `/v1/job_template_group`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const jobTemplateGroups = response.data ? response.data: [];
                commit('setJobTemplateGroups', jobTemplateGroups)
            });
        },
        async createJobTemplateGroup({rootState}, payload) {
            return axios.post(rootState.surfaceUrl + "/v1/job_template_group", payload, {
                    headers: {
                        Authorization: `Bearer ${rootState.accessToken}`,
                    },
            })
        },
        async updateJobTemplateGroup({rootState}, payload) {
            return axios.put(rootState.surfaceUrl + "/v1/job_template_group/" + payload.id, payload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async deleteJobTemplateGroup({rootState}, id) {
            return axios.delete(rootState.surfaceUrl + "/v1/job_template_group/" + id, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async readJobTemplateGroup({rootState}, id) {
            return axios.get(rootState.surfaceUrl + "/v1/job_template_group/" + id, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async getJobTemplateGroupMappings({rootState, commit}, job_template_id) {
            return axios.get(rootState.surfaceUrl + `/v1/job_template_group/${job_template_id}/job_template`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                const jobTemplateGroupMapList = response.data ? response.data: [];
                commit('setJobTemplateGroupMapList', jobTemplateGroupMapList)
                return response
            });
        },
        async createJobTemplateGroupMapping({rootState}, payload) {
            return axios.post(rootState.surfaceUrl +`/v1/job_template_group/${payload.job_template_group_id}/job_template`, payload, {
                    headers: {
                        Authorization: `Bearer ${rootState.accessToken}`,
                    },
            })
        },
        async updateJobTemplateGroupMapping({rootState}, payload) {
            return axios.put(rootState.surfaceUrl + `/v1/job_template_group/${payload.job_template_group_id}/job_template/${payload.job_template_id}`, payload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async deleteJobTemplateGroupMapping({rootState}, payload) {
            return axios.delete(rootState.surfaceUrl +`/v1/job_template_group/${payload.job_template_group_id}/job_template/${payload.job_template_id}`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async readJobTemplateGroupMapping({rootState}, payload) {
            return axios.get(rootState.surfaceUrl + `/v1/job_template_group/${payload.job_template_group_id}/job_template/${payload.job_template_id}`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async fetchAgentsUsageData({rootState, dispatch, state, commit}, usageType) {
            // Reset only the specific usageType portion of agentsUsageData
            commit('setAgentsUsageData', { 
                ...state.agentsUsageData, 
                [usageType]: {} 
            });

            let payload = {
                startDate: state.agentsUsageDataStartDate,
                endDate: state.agentsUsageDataEndDate,
                product: 'agents',
                usageType: usageType                
            };
            await dispatch('usage/getProductUsage', payload, { root: true }).then((data) => {
                if(data && data[payload.product]) {
                    // Update only the specific usageType portion while preserving other data
                    commit('setAgentsUsageData', {
                        ...state.agentsUsageData,
                        [usageType]: data[payload.product]
                    });
                }
            });            
        },
        setAgentsUsageDataStartDate({commit}, startDate) {
            commit('setAgentsUsageDataStartDate', startDate);
        },
        setAgentsUsageDataEndDate({commit}, endDate) {
            commit('setAgentsUsageDataEndDate',endDate);
        },
        setAgentsUsageMonth({commit}, month) {
            commit('setAgentsUsageMonth', month)
        },
        // mcpUsageData is keyed by server ('mcp', 'mcp_control', ...) so both servers
        // can be cached side by side while sharing one date range.
        async fetchMcpUsageData({rootState, dispatch, state, commit}, {server, usageType}) {
            // Reset only the specific usageType portion of this server's data
            commit('setMcpUsageData', {
                ...state.mcpUsageData,
                [server]: {
                    ...state.mcpUsageData[server],
                    [usageType]: {}
                }
            });

            let payload = {
                startDate: state.mcpUsageDataStartDate,
                endDate: state.mcpUsageDataEndDate,
                product: server,
                usageType: usageType,
                subLabel: true
            };
            await dispatch('usage/getProductUsage', payload, { root: true }).then((data) => {
                if(data && data[payload.product]) {
                    // Update only the specific usageType portion while preserving other data
                    commit('setMcpUsageData', {
                        ...state.mcpUsageData,
                        [server]: {
                            ...state.mcpUsageData[server],
                            [usageType]: data[payload.product]
                        }
                    });
                }
            });            
        },
        // Every cached server was fetched under the old range, so dropping the whole
        // cache is what stops a server switch from rendering stale counts against
        // the new dates. Only the server in view refetches immediately.
        setMcpUsageDataStartDate({commit}, startDate) {
            commit('setMcpUsageData', {});
            commit('setMcpUsageDataStartDate', startDate);
        },
        setMcpUsageDataEndDate({commit}, endDate) {
            commit('setMcpUsageData', {});
            commit('setMcpUsageDataEndDate',endDate);
        },
        setMcpUsageMonth({commit}, month) {
            commit('setMcpUsageMonth', month)
        },
        fetchInfraUsageData({rootState, dispatch, state, commit}, usageType) {
            // Reset only the specific usageType portion of infraUsageData
            commit('setInfraUsageData', { 
                ...state.infraUsageData, 
                [usageType]: {} 
            });

            let payload = {
                startDate: state.infraUsageDataStartDate,
                endDate: state.infraUsageDataEndDate,
                product: 'infrastructure',
                usageType: usageType                
            };
            dispatch('usage/getProductUsage', payload, { root: true }).then((data) => {
                if(data && data[payload.product]) {
                    // Update only the specific usageType portion while preserving other data
                    commit('setInfraUsageData', {
                        ...state.infraUsageData,
                        [usageType]: data[payload.product]
                    });
                }
            });            
        },
        setInfraUsageDataStartDate({commit}, startDate) {
            commit('setInfraUsageDataStartDate', startDate);
        },
        setInfraUsageDataEndDate({commit}, endDate) {
            commit('setInfraUsageDataEndDate',endDate);
        },
        setUsageMonth({commit}, month) {
            commit('setUsageMonth', month)
        },
        async fetchConfigurations({rootState, state, commit}){
            return axios.get(rootState.surfaceUrl+`/v1/cids/${rootState.currentClient.cid}/input_streams`,{
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`
                }
            }).then((response)=>{
                let configurations = response.data
                commit('setConfigurations', configurations)
            }).catch((e) => {
                commit('setConfigurations', [])
            })
        },
        async createConfiguration({rootState}, configurationPayload){
            return axios.post(rootState.surfaceUrl+`/v1/cids/${rootState.currentClient.cid}/input_streams`,configurationPayload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`
                }
            })
        },
        async updateConfiguration({rootState}, configurationPayload) {
            return axios.put(rootState.surfaceUrl + `/v1/cids/${rootState.currentClient.cid}/input_streams/` + configurationPayload.id, configurationPayload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async deleteConfiguration({rootState}, id) {
            return axios.delete(rootState.surfaceUrl + `/v1/cids/${rootState.currentClient.cid}/input_streams/` + id, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async readConfiguration({rootState}, id) {
            return axios.get(rootState.surfaceUrl + `/v1/cids/${rootState.currentClient.cid}/input_streams/` + id, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        setEntitlementUsageMonth({commit}, month) {
            commit('setEntitlementUsageMonth', month )
        },
        async fetchDocLinks({rootState, commit}) {
            return axios.get(rootState.defaultSurfaceUrl + '/v2/doc_links', {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                commit('setDocLinks', response.data)
            }).catch((e) => {
                commit('setDocLinks', [])
            })
        },
        async createDocLink({rootState}, payload) {
            return axios.post(rootState.defaultSurfaceUrl + '/v2/doc_links', payload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async updateDocLink({rootState}, payload) {
            return axios.put(rootState.defaultSurfaceUrl + `/v2/doc_links/${payload.title}`, payload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async deleteDocLink({rootState}, title) {
            return axios.delete(rootState.defaultSurfaceUrl + `/v2/doc_links/${title}`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async readDocLink({rootState}, title) {
            return axios.get(rootState.defaultSurfaceUrl + `/v2/doc_links/${title}`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            })
        },
        async fetchCheckListStatus({rootState}) {
            return axios.get(rootState.clientMgmtUrl + `/v1/cids/${rootState.currentClient.cid}/check_list`, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                return response.data;
            }).catch((error) => {
                console.error("Error fetching checklist status:", error);
                throw error;
            });
        },
        async updateCheckListStatus({rootState}, payload) {
            return axios.put(rootState.clientMgmtUrl + `/v1/cids/${rootState.currentClient.cid}/check_list`, payload, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                return response.data;
            }).catch((error) => {
                console.error("Error fetching checklist status:", error);
                throw error;
            });
        },

    }
}

export default store